// src/components/Landing.js
import React from 'react'
import CaptureIcon from './CaptureIcon'
import QuickCaptureModal from './QuickCaptureModal'
import FullScreenCamera from './FullScreenCamera'
import { saveMoment } from '../momentsService'
import { supabase } from '../supabaseClient'
import { CITIES, DAYS } from '../data/content'
import { useAvatar } from '../hooks/useAvatar'
import { MAPS_LINES } from './TransitMap'
import shareImage from '../utils/shareImage'

const CITY_LIST = [
  { id: 'nyc',    color: '#EE352E', fg: '#fff', letter: 'N', locked: false },
  { id: 'paris',  color: '#5F2D8A', fg: '#fff', letter: 'P', locked: false },
  { id: 'london', color: '#E32017', fg: '#fff', letter: 'L', locked: false },
  { id: 'milan',  color: '#CC0000', fg: '#fff', letter: 'M', locked: false },
  { id: 'berlin', color: '#F0CA00', fg: '#000', letter: 'B', locked: false },
  { id: 'madrid', color: '#E4032E', fg: '#fff', letter: 'M', locked: false },
]

const CITY_EXTRAS = {
  berlin: { name: 'Berlin' },
  madrid: { name: 'Madrid' },
}

export default function Landing({ session, theme, toggleTheme, onSelectCity, cityProgress = {}, getNextStation, onShowDashboard, onShowMoments }) {
  const { displayName, avatarUrl } = useAvatar(session)
  const [activeCity, setActiveCity] = React.useState('nyc')
  const [quickCapture, setQuickCapture] = React.useState(false)
  const [showCamera, setShowCamera] = React.useState(false)
  const [momentToast, setMomentToast] = React.useState(false)
  const [avatarPulse, setAvatarPulse] = React.useState(false)
  const [sharing, setSharing] = React.useState(false)
  const shareCardRef = React.useRef(null)

  const getGreeting = () => {
    const h = new Date().getHours()
    if (h >= 5 && h < 12) return { text: 'Good Morning', emoji: '☀️' }
    if (h >= 12 && h < 17) return { text: 'Good Afternoon', emoji: '✦' }
    if (h >= 17 && h < 21) return { text: 'Good Evening', emoji: '🌆' }
    return { text: 'Good Night', emoji: '🌙' }
  }

  const greeting = getGreeting()
  const firstName = (displayName || '').split(' ')[0] || ''
  const user = session.user
  const signOut = () => supabase.auth.signOut()
  const activeCityMeta = CITY_LIST.find(c => c.id === activeCity)
  const nextStation = getNextStation ? getNextStation(activeCity) : 1
  const activeProgress = cityProgress[activeCity] || 0
  const cityDays = DAYS[activeCity] || []
  const TOTAL = 28

  return (
    <div className="landing-page">

      {/* ── Fixed top bar: capture · The Magic · avatar ── */}
      <div className="landing-topnav">
        <button className="landing-capture-btn landing-topnav-capture" onClick={() => setShowCamera(true)}>
          <CaptureIcon color={activeCityMeta?.color || '#EE352E'} size={22} />
        </button>
        <div className="landing-topnav-wordmark">The Magic</div>
        <button
          className={`landing-avatar-bubble landing-topnav-avatar${avatarPulse ? ' avatar-pulse' : ''}`}
          onClick={onShowDashboard}
        >
          <div className="db-avatar-ring-wrap" style={{ width: 44, height: 44 }}>
            <svg className="db-avatar-arc" viewBox="0 0 100 100" width="44" height="44">
              {(() => {
                const cx = 50, cy = 50, r = 44
                const total = 28, segmentAngle = 360 / total, gap = 2
                const progress = cityProgress[activeCity] || 0
                const days = DAYS[activeCity] || []
                const activeMeta = CITY_LIST.find(ci => ci.id === activeCity)
                return Array.from({ length: total }, (_, i) => {
                  const done = i < progress
                  const current = i === progress
                  const color = done ? (days[i]?.color || activeMeta?.color || '#EE352E') : current ? '#22c55e' : 'var(--border)'
                  const startAngle = -90 + i * segmentAngle + gap / 2
                  const endAngle = startAngle + segmentAngle - gap
                  const toRad = deg => (deg * Math.PI) / 180
                  const x1 = cx + r * Math.cos(toRad(startAngle)), y1 = cy + r * Math.sin(toRad(startAngle))
                  const x2 = cx + r * Math.cos(toRad(endAngle)),   y2 = cy + r * Math.sin(toRad(endAngle))
                  return (
                    <path key={i}
                      d={`M ${x1} ${y1} A ${r} ${r} 0 ${segmentAngle - gap > 180 ? 1 : 0} 1 ${x2} ${y2}`}
                      fill="none" stroke={color}
                      strokeWidth={done || current ? 4.5 : 3}
                      strokeLinecap="round"
                      opacity={done || current ? 1 : 0.25}
                    />
                  )
                })
              })()}
            </svg>
            <div className="db-avatar" style={{ overflow: 'hidden' }}>
              {avatarUrl || user.user_metadata?.avatar_url
                ? <img src={avatarUrl || user.user_metadata?.avatar_url} alt="avatar" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
                : <span>{(displayName || 'U')[0].toUpperCase()}</span>
              }
            </div>
          </div>
        </button>
      </div>

      {/* Main scrollable content */}
      <div className="landing-wrap">

        {/* Greeting — below topnav on mobile, below title on desktop */}
        <div className="landing-header">
          <div className="landing-title">The Magic</div>
          <div className="landing-greeting">
            {greeting.text}{firstName ? `, ${firstName}` : ''} {greeting.emoji}
          </div>
        </div>

        {/* Progress line */}
        <div className="landing-metro-preview">
          <div className="landing-metro-preview-label" style={{ color: activeCityMeta?.color || '#EE352E' }}>
            {(CITIES[activeCity] || CITY_EXTRAS[activeCity] || {}).name} Line
            <span style={{ color: 'var(--text4)', fontWeight: 400, letterSpacing: 0, textTransform: 'none', fontSize: 8, marginLeft: 8 }}>
              {activeProgress} of {TOTAL}
            </span>
          </div>
          <div className="landing-metro-track">
            {Array.from({ length: TOTAL }, (_, i) => {
              const n = i + 1
              const done = n < nextStation
              const current = n === nextStation && activeProgress > 0
              const isMilestone = n === 7 || n === 14 || n === 21 || n === 28
              const stopColor = cityDays[i]?.color || activeCityMeta?.color
              const nextStopColor = cityDays[i + 1]?.color || activeCityMeta?.color
              return (
                <React.Fragment key={i}>
                  <div
                    className={'landing-metro-stop' + (done ? ' done' : current ? ' current' : '') + (isMilestone ? ' milestone' : '')}
                    style={done ? { background: stopColor, borderColor: stopColor } : {}}
                  />
                  {i < TOTAL - 1 && (
                    <div
                      className="landing-metro-connector"
                      style={done && n < nextStation - 1 ? { background: nextStopColor } : done ? { background: stopColor, opacity: 0.4 } : {}}
                    />
                  )}
                </React.Fragment>
              )
            })}
          </div>
        </div>

        {/* Capture CTA */}
        <button
          onClick={() => setShowCamera(true)}
          style={{
            width: '100%', maxWidth: 400,
            margin: '0 0 12px',
            border: '1.5px dashed var(--border)',
            borderRadius: 12, padding: '9px 14px',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            background: 'transparent', cursor: 'pointer', fontFamily: 'var(--fn)',
          }}
        >
          <CaptureIcon color={activeCityMeta?.color || '#EE352E'} size={14} />
          <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: 3, color: 'var(--text4)', textTransform: 'uppercase' }}>
            CAPTURE THE MOMENT
          </span>
        </button>

        {/* City lines */}
        <p className="landing-lines-label">Choose your line</p>
        <div className="landing-lines">
          {CITY_LIST.map(({ id, color, letter, locked }) => {
            const city = CITIES[id] || CITY_EXTRAS[id] || {}
            const isActive = activeCity === id
            const stationNum = getNextStation ? getNextStation(id) : 1
            const stationName = DAYS[id]?.[stationNum - 1]?.title
            const inProgress = !locked && (cityProgress[id] || 0) > 0
            return (
              <div
                key={id}
                className={'landing-line-card' + (isActive ? ' active' : '') + (locked ? ' locked' : '')}
                style={{ '--c': color }}
                onClick={locked ? undefined : () => {
                  if (isActive) onSelectCity(id)
                  else setActiveCity(id)
                }}
              >
                <div className="landing-line-bullet">{letter}</div>
                <div className="landing-line-info">
                  <div className="landing-line-name">{city.name}</div>
                  <div className="landing-line-status">
                    {locked ? 'Coming soon' : inProgress ? `Station ${stationNum} · ${stationName || ''}` : 'Begin your journey'}
                  </div>
                </div>
                {locked ? <span className="landing-lock-icon">🔒</span> : <span className="landing-line-arrow">→</span>}
              </div>
            )
          })}
        </div>

        {/* Share The Magic */}
        <button
          onClick={async () => {
            if (sharing) return
            setSharing(true)
            try {
              const el = shareCardRef.current
              if (!el) throw new Error('no ref')
              const h2c = await new Promise(resolve => {
                if (window.html2canvas) { resolve(window.html2canvas); return }
                const s = document.createElement('script')
                s.src = 'https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js'
                s.onload = () => resolve(window.html2canvas)
                s.onerror = () => resolve(null)
                document.head.appendChild(s)
              })
              if (!h2c) throw new Error('html2canvas unavailable')
              const canvas = await h2c(el, {
                scale: 3, useCORS: true, allowTaint: true,
                backgroundColor: '#0d1117', logging: false,
                width: el.offsetWidth, height: el.offsetHeight,
              })
              const blob = await new Promise(res => canvas.toBlob(res, 'image/jpeg', 0.93))
              const cityName = (CITIES[activeCity] || CITY_EXTRAS[activeCity] || {}).name || activeCity
              const text = `I'm on Day ${nextStation} of the ${cityName} Line — The Magic Journey ✦ ${window.location.host}`
              await shareImage(blob, `magic-${activeCity}-map.jpg`, text)
            } catch (e) {
              if (e.name !== 'AbortError') console.error('Share error:', e)
            }
            setSharing(false)
          }}
          style={{
            width: '100%', maxWidth: 400,
            margin: '8px 0 0',
            border: '1.5px solid var(--border)',
            borderRadius: 12, padding: '11px 14px',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            background: 'transparent', cursor: sharing ? 'default' : 'pointer', fontFamily: 'var(--fn)',
            WebkitTapHighlightColor: 'transparent', opacity: sharing ? 0.5 : 1,
          }}
        >
          <span style={{ fontSize: 13 }}>✦</span>
          <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: 3, color: 'var(--text4)', textTransform: 'uppercase' }}>
            {sharing ? 'SHARING…' : 'SHARE THE MAGIC'}
          </span>
        </button>

        {/* Footer */}
        <div className="landing-bottom">
          <button className="landing-quiet-btn" onClick={toggleTheme}>
            {theme === 'dark' ? '☀️' : '🌑'}
          </button>
          <button className="landing-quiet-btn landing-signout-btn" onClick={signOut}>Sign out</button>
        </div>

      </div>

      {/* Hidden share card — captured by html2canvas */}
      {(() => {
        const cityName = (CITIES[activeCity] || CITY_EXTRAS[activeCity] || {}).name || activeCity
        const cityColor = activeCityMeta?.color || '#EE352E'
        const lines = MAPS_LINES[activeCity] || []
        return (
          <div
            ref={shareCardRef}
            style={{
              position: 'fixed', top: -9999, left: -9999,
              width: 320, background: '#0d1117',
              fontFamily: 'HelveticaNow, Helvetica Neue, Helvetica, Arial, sans-serif',
              padding: '32px 28px 28px', boxSizing: 'border-box',
            }}
          >
            {/* Transit map SVG */}
            <div style={{ width: '100%', aspectRatio: '5/3', marginBottom: 24, background: '#0d1117' }}>
              <svg viewBox="0 0 200 160" style={{ width: '100%', height: '100%', display: 'block' }}>
                {lines.map(line => (
                  <path
                    key={line.id}
                    d={line.d}
                    stroke={line.color}
                    strokeWidth={2.5}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    fill="none"
                  />
                ))}
              </svg>
            </div>
            {/* Eyebrow */}
            <div style={{ fontSize: 9, letterSpacing: 3, color: 'rgba(255,255,255,0.35)', fontWeight: 700, marginBottom: 8, textTransform: 'uppercase' }}>
              The Magic Journey
            </div>
            {/* City name */}
            <div style={{ fontSize: 32, fontWeight: 900, color: '#fff', letterSpacing: -1, lineHeight: 1, marginBottom: 6 }}>
              {cityName}
            </div>
            {/* Line + station */}
            <div style={{ fontSize: 11, fontWeight: 700, color: cityColor, letterSpacing: 1.5, marginBottom: 24, textTransform: 'uppercase' }}>
              Line · Station {nextStation}
            </div>
            {/* URL */}
            <div style={{ fontSize: 9, letterSpacing: 2, color: 'rgba(255,255,255,0.25)', fontWeight: 600 }}>
              {window.location.host}
            </div>
          </div>
        )
      })()}

      {/* Modals */}
      {showCamera && (
        <FullScreenCamera
          cityColor={activeCityMeta?.color || '#EE352E'}
          onClose={(reason) => {
            setShowCamera(false)
            if (reason === 'picker') setQuickCapture(true)
          }}
          onCapture={async (dataUrl) => {
            setShowCamera(false)
            await saveMoment({ userId: session.user.id, imageUrl: dataUrl, city: activeCity, dayNumber: null })
            setMomentToast(true)
            setTimeout(() => setMomentToast(false), 3000)
          }}
        />
      )}

      {quickCapture && (
        <QuickCaptureModal
          session={session}
          currentCity={activeCity}
          defaultDay={cityProgress[activeCity] || 1}
          allEntries={{}}
          hideStationPicker={true}
          onClose={() => setQuickCapture(false)}
          onSaved={() => {
            setQuickCapture(false)
            setMomentToast(true)
            setAvatarPulse(true)
            setTimeout(() => setMomentToast(false), 3000)
            setTimeout(() => setAvatarPulse(false), 2000)
          }}
        />
      )}

      {momentToast && (
        <div className="landing-moment-toast">✦ Moment captured</div>
      )}
    </div>
  )
}
